import classNames from 'classnames';
import {
  type ChangeEvent,
  type ComponentPropsWithoutRef,
  forwardRef,
  useCallback,
  useContext,
} from 'react';

import { FormComponentContext } from '../index.js';

export interface SelectProps extends Omit<ComponentPropsWithoutRef<'select'>, 'onChange'> {
  /**
   * Whether or not the select field has an error.
   */
  readonly error?: unknown;

  /**
   * Whether or not the element should take as much space as it can.
   */
  readonly fullWidth?: boolean;

  /**
   * Indicate the select box is in a loading state.
   */
  readonly loading?: boolean;

  /**
   * This is fired when the input value has changed.
   */
  readonly onChange?: (event: ChangeEvent<HTMLSelectElement>, value: string | string[]) => void;
}

/**
 * A Bulma styled form select element.
 */
export const Select = forwardRef<HTMLSelectElement, SelectProps>(
  (
    { className, error, fullWidth, loading, multiple, name, onChange, id = name, ...props },
    ref,
  ) => {
    const aria = useContext(FormComponentContext);
    const handleChange = useCallback(
      (event: ChangeEvent<HTMLSelectElement>) => {
        onChange?.(
          event,
          multiple
            ? Array.from(event.currentTarget.selectedOptions, ({ value }) => value)
            : event.currentTarget.value,
        );
      },
      [multiple, onChange],
    );

    return (
      <div
        className={classNames('select', className, {
          'is-danger': error,
          'is-loading': loading,
          'is-fullwidth': fullWidth,
          'is-multiple': multiple,
        })}
      >
        <select
          className={classNames({ 'is-fullwidth': fullWidth })}
          id={id}
          multiple={multiple}
          name={name}
          onChange={handleChange}
          ref={ref}
          {...aria}
          {...props}
        />
      </div>
    );
  },
);
